import { useState } from "react";
import type { ReactNode } from "react";
import { Pencil, Trash2 } from "lucide-react";

export { BentoSelect } from "./bento-select";
export { BentoColorInput } from "./bento-color-input";
export { BentoDateInput } from "./bento-date-input";
export { BentoTimeInput } from "./bento-time-input";

/**
 * PagedList — perf với dữ liệu lớn: chỉ render `pageSize` item đầu, phần còn
 * lại sau nút "Hiện thêm". Render-prop nhận từng item (nhớ tự gắn key).
 * Limit là state cục bộ nên đổi filter/unmount sẽ tự reset — chấp nhận được.
 */
export function pagedListWindow<T>(items: T[], limit: number) {
  const visible = items.length > limit ? items.slice(0, limit) : items;
  return {
    visible,
    remaining: Math.max(0, items.length - visible.length),
  };
}

export function PagedList<T>({
  items,
  pageSize = 50,
  moreClassName = "",
  children,
}: {
  items: T[];
  pageSize?: number;
  /** Extra classes cho nút "Hiện thêm" (vd: span hết hàng trong grid). */
  moreClassName?: string;
  children: (item: T, index: number) => ReactNode;
}) {
  const [limit, setLimit] = useState(pageSize);
  const { visible, remaining } = pagedListWindow(items, limit);
  return (
    <>
      {visible.map(children)}
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setLimit((l) => l + pageSize)}
          className={`w-full rounded-xl bg-ink-100 py-2 text-[12px] font-bold text-ink-600 hover:bg-ink-200 transition ${moreClassName}`}
        >
          Hiện thêm ({remaining})
        </button>
      )}
    </>
  );
}

/** "1234567" → "1.234.567" (vi-VN, không thập phân) — dùng chung mọi ô nhập tiền. */
export function formatAmountInput(digits: string): string {
  const n = Number(String(digits).replace(/\D/g, ""));
  return n > 0 ? n.toLocaleString("vi-VN") : "";
}

/**
 * BentoMoneyInput — ô nhập tiền chuẩn module Tài chính: chỉ nhận chữ số,
 * tự bỏ số 0 đứng đầu ("0300000" → "300000"), hiển thị phân cách nghìn.
 * onChange nhận CHUỖI CHỮ SỐ thô ("120000") để payload Number() được ngay.
 */
export function BentoMoneyInput({
  value,
  onChange,
  className = "",
  placeholder = "0",
  ...props
}: {
  value: unknown;
  onChange: (digits: string) => void;
  className?: string;
  placeholder?: string;
  "aria-label"?: string;
  id?: string;
}) {
  return (
    <input
      {...props}
      inputMode="numeric"
      value={formatAmountInput(String(value ?? ""))}
      placeholder={placeholder}
      onChange={(e) =>
        onChange(e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 12))
      }
      className={`w-full rounded-xl border border-ink-200 bg-white px-3 py-2.5 text-[14px] outline-none focus:border-brand-500 ${className}`}
    />
  );
}

export const fullDate = (s: string) =>
  new Date(s).toLocaleDateString("vi-VN", { day: "2-digit", month: "short", year: "numeric" });
export const moneyShort = (n: number) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(n % 1e6 ? 1 : 0)}tr` : `${Math.round(n / 1e3)}k`;
export const PRIO: Record<string, string> = { high: "#f43f5e", medium: "#f59e0b", low: "#94a3b8" };
export const PRIO_LABEL: Record<string, string> = { high: "Cao", medium: "TB", low: "Thấp" };

export function Tile({ children, className = "", onClick, "data-testid": testId }: { children: ReactNode; className?: string; onClick?: () => void; "data-testid"?: string }) {
  // Dark mode: subtle brand-tinted ring + elevation shadow so cards don't flatten
  // into the slate background (light mode unchanged — dark: variants only).
  return <div data-testid={testId} onClick={onClick} className={`rounded-3xl bg-white p-5 dark:shadow-[0_2px_10px_rgba(0,0,0,0.35)] dark:ring-1 dark:ring-brand-400/15 ${className}`}>{children}</div>;
}

// Wrap row actions so clicking them doesn't trigger a parent card's onClick.
export function StopClick({ children }: { children: ReactNode }) {
  return <span onClick={(e) => e.stopPropagation()}>{children}</span>;
}

export function EmptyState({
  icon,
  title,
  hint,
  cta,
}: {
  icon?: ReactNode;
  title: string;
  hint: string;
  /** Optional call-to-action button rendered below the hint. Back-compat: callers without cta are unaffected. */
  cta?: { label: string; onClick: () => void };
}) {
  return (
    <div className="rounded-3xl border border-dashed border-ink-300 bg-white/50 py-12 text-center">
      {icon && <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-100 text-brand-600">{icon}</div>}
      <p className="font-display text-[15px] font-bold">{title}</p>
      <p className="mx-auto mt-1 max-w-xs text-[13px] text-ink-500">{hint}</p>
      {cta && (
        <button
          onClick={cta.onClick}
          className="gbrand mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-bold text-white"
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}

export function TileHead({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h3 className="font-display text-[16px] font-bold">{title}</h3>
      {action}
    </div>
  );
}

export function ProgressBar({ percent, over = false, warn = false }: { percent: number; over?: boolean; warn?: boolean }) {
  // over (đỏ) > warn (hổ phách, sắp chạm ngưỡng) > bình thường (brand).
  const fill = over ? "bg-rose-500" : warn ? "bg-amber-500" : "bg-brand-500";
  return (
    <div className="h-2 overflow-hidden rounded-full bg-ink-200">
      <div className={`h-full rounded-full ${fill}`} style={{ width: `${percent}%` }} />
    </div>
  );
}

export function IconButton({ icon, label, onClick }: { icon: "edit" | "delete"; label: string; onClick?: () => void }) {
  const Icon = icon === "edit" ? Pencil : Trash2;
  const hover = icon === "delete" ? "hover:bg-rose-50 hover:text-rose-500" : "hover:bg-ink-200/60 hover:text-ink-900";
  return (
    <button aria-label={label} title={label} onClick={onClick} className={`grid h-7 w-7 place-items-center rounded-lg text-ink-500 ${hover}`}>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export function RowActions({ onEdit, onDelete }: { onEdit?: () => void; onDelete?: () => void }) {
  return (
    <div className="flex shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100">
      <IconButton icon="edit" label="Sửa" onClick={onEdit} />
      <IconButton icon="delete" label="Xoá" onClick={onDelete} />
    </div>
  );
}
